import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { PazeService, ProviderName } from '@fgb/core';
import { BaseExpressCheckoutComponent, EXPRESS_CHECKOUT_SETTINGS, ExpressCheckoutSettings } from '../../base-express-checkout';
import { PipesModule } from '@fgb/portal-component-library/src/lib/pipes';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';

/**
 * Paze express checkout provider. Implements the Auto-Initiate with Express Pay flow: loads +
 * initializes the Paze SDK, checks the consumer has a Paze wallet, and on submit() runs
 * checkout -> complete -> submit, emitting the outcome up to the ExpressCheckoutComponent
 * container. A consumer with no wallet falls back to the sign-up path rather than hiding the
 * button — see onInitialized(). Rendered only when Paze is registered against
 * EXPRESS_CHECKOUT_PROVIDERS and configured via config.providerSettings.
 */
@Component({
  selector: 'fgbcl-paze-express-checkout',
  standalone: true,
  templateUrl: './paze-express-checkout.component.html',
  styleUrl: './paze-express-checkout.component.scss',
  imports: [CommonModule, PipesModule, FGBSharedModule],
  providers: [
    {
      provide: EXPRESS_CHECKOUT_SETTINGS,
      useValue: (settings: any): ExpressCheckoutSettings => ({
        settings: {
          client: {
            id: settings.id,
            profileId: settings.profileId,
            name: settings.merchantName,
          },
        },
      }),
    },
  ],
})
export class PazeExpressCheckoutComponent extends BaseExpressCheckoutComponent {
  private cdr = inject(ChangeDetectorRef);
  private pazeService: PazeService;
  loading: boolean = true;

  /**
   * The button's artwork, which differs by what the press is for.
   *
   * 'card-token' is the payment page, where the press adds a card to the
   * member's wallet - so the button says so ("Add a card with paze"). Every
   * other mode is paying an order, where that wording would be wrong, and the
   * plain provider mark is used instead.
   *
   * The provider mark is named by provider id, the same convention the saved
   * card rows use, so Paze's is 115.svg.
   */
  get logoAsset(): string {
    return this.context?.mode === 'card-token' ? 'providers/paze-text.svg' : `providers/${ProviderName.Paze}.svg`;
  }

  constructor() {
    const pazeService = inject(PazeService);
    super(ProviderName.Paze, pazeService);
    this.pazeService = pazeService;
  }

  /**
   * Post-init eligibility gate: ask Paze whether this consumer already has a wallet before the
   * button is offered. Note the provider stays `ready` either way — an ineligible consumer can still
   * press it, they just get the Paze registration path instead of a one-press pay.
   */
  protected override async onInitialized(): Promise<void> {
    const eligible = await this.pazeService.canCheckout({
      emailAddress: this.context?.emailAddress,
      mobileNumber: this.context?.mobileNumber,
    });

    this.loading = false;

    if (!eligible) {
      // Drop the identifiers we just checked: with neither on the context, Paze cannot resolve an
      // existing wallet and opens its sign-up modal instead of failing the lookup a second time.
      this.context = { ...this.context, emailAddress: undefined, mobileNumber: undefined };
      return;
    }

    // Runs after an await in a dynamically-created component, so it is outside the container's
    // change detection pass: flush manually or `ready`/context changes never reach the view.
    this.cdr.detectChanges();
  }
}
